import { BaseApiService, apiClientFactory } from '@doccareplus/api-client';
import { ACTIVE_LINK_STORAGE_KEY } from '../domain/activeProfile';

// HTTP adapter for the patient-portal profile endpoints (api/portal/*). Auth is by PatientPortalLink
// ownership — the requesting user is stamped server-side from the JWT, never sent by the client. All JSON is
// camelCase; these types mirror the C# Contracts.Portal records 1:1.

export type PortalRelationship = 'Self' | 'Parent' | 'Guardian' | 'Caregiver';

/** Mirrors Contracts.Portal.Common.PortalProfileDto (masked, display-safe). */
export interface PortalProfileDto {
  linkId: string;
  patientId: string;
  clinicId: string;
  clinicName: string;
  patientDisplayName: string;
  displayAlias: string | null;
  relationshipType: PortalRelationship;
  dobYear: number | null;
  maskedPhone: string | null;
  maskedEmail: string | null;
  isActiveSelection: boolean;
  isArchived: boolean;
}

/** Mirrors Contracts.Portal.Common.PortalFoundProfileDto (pre-claim, minimal masked identity). */
export interface PortalFoundProfileDto {
  patientId: string;
  clinicId: string;
  clinicName: string;
  patientDisplayName: string;
  dobYear: number | null;
  maskedPhone: string | null;
  maskedEmail: string | null;
}

export interface ListPortalProfilesResponse {
  profiles: PortalProfileDto[];
  activeLinkId: string | null;
}

export interface FindPortalProfilesResponse {
  profiles: PortalFoundProfileDto[];
}

// Home-screen-only clinical/appointment summary for the active profile. Mirrors Contracts.Portal.GetHome.*
// — deliberately separate from PortalProfileDto (shared across List/Found/Claim, never clinical data).
export interface PortalNextAppointmentDto {
  appointmentId: string;
  startTime: string;
  durationMinutes: number;
  status: string;
  reason: string | null;
}

export interface PortalRecentVisitDto {
  visitDate: string;
  diagnosis: string | null;
  providerDisplayName: string;
}

export interface PortalMedicationItemDto {
  medicineName: string;
  detail: string | null;
  occurred: string | null;
}

export interface PortalHomeSummaryDto {
  allergySummary: string | null;
  clinicPhone: string | null;
  timezone: string;
  nextAppointment: PortalNextAppointmentDto | null;
  recentVisits: PortalRecentVisitDto[];
  currentMedications: PortalMedicationItemDto[];
}

export interface PortalHomeResponse {
  activeProfile: PortalProfileDto | null;
  profiles: PortalProfileDto[];
  summary: PortalHomeSummaryDto | null;
  isActiveProfilePortalAvailable: boolean;
}

/** Mirrors Contracts.Portal.Common.PortalDisconnectedProfileDto — read-only disconnection history. */
export interface PortalDisconnectedProfileDto {
  linkId: string;
  clinicName: string;
  patientDisplayName: string;
  displayAlias: string | null;
  relationshipType: PortalRelationship;
  disconnectedAt: string;
}

export interface ListDisconnectedProfilesResponse {
  profiles: PortalDisconnectedProfileDto[];
}

export interface SetActivePortalProfileResponse {
  activeLinkId: string;
}

/** Mirrors Contracts.Portal.RequestDetailCorrection.PatientDetailCorrectionRequestDto. */
export interface PatientDetailCorrectionRequestDto {
  id: string;
  note: string;
  status: string;
  requestedAt: string;
}

export interface RequestDetailCorrectionBody {
  note: string;
}

export interface ClaimPortalProfileBody {
  patientId: string;
  relationshipType: PortalRelationship;
  displayAlias?: string | null;
}

class PortalProfilesApiService extends BaseApiService {
  constructor() {
    super('/portal');
  }

  home(): Promise<PortalHomeResponse> {
    return this.get<PortalHomeResponse>('/home');
  }

  list(): Promise<ListPortalProfilesResponse> {
    return this.get<ListPortalProfilesResponse>('/profiles');
  }

  found(): Promise<FindPortalProfilesResponse> {
    return this.get<FindPortalProfilesResponse>('/profiles/found');
  }

  disconnected(): Promise<ListDisconnectedProfilesResponse> {
    return this.get<ListDisconnectedProfilesResponse>('/profiles/disconnected');
  }

  claim(body: ClaimPortalProfileBody): Promise<PortalProfileDto> {
    return this.post<PortalProfileDto, ClaimPortalProfileBody>('/profiles/claim', body);
  }

  select(linkId: string): Promise<SetActivePortalProfileResponse> {
    return this.post<SetActivePortalProfileResponse>(`/profiles/${linkId}/select`);
  }

  disconnect(linkId: string): Promise<boolean> {
    return this.post<boolean>(`/profiles/${linkId}/disconnect`);
  }

  requestDetailCorrection(body: RequestDetailCorrectionBody): Promise<PatientDetailCorrectionRequestDto> {
    return this.post<PatientDetailCorrectionRequestDto, RequestDetailCorrectionBody>('/details/correction', body);
  }
}

export const portalProfilesApi = new PortalProfilesApiService();

// Side-effecting helper: persist the selected profile and apply it as the per-request header so subsequent
// per-patient reads resolve to it. Passing null clears the selection (e.g. after disconnecting the active one).
export function applyActiveProfileHeader(linkId: string | null): void {
  if (linkId) {
    localStorage.setItem(ACTIVE_LINK_STORAGE_KEY, linkId);
    apiClientFactory.addHeader('X-Portal-Link-Id', linkId);
  } else {
    localStorage.removeItem(ACTIVE_LINK_STORAGE_KEY);
    apiClientFactory.removeHeader('X-Portal-Link-Id');
  }
}
